import { ChildEntity, ChildType, CreateFolderRequest, EntityType, GetFolderResponse, ObjectId, RoleAssignment } from '../types';
import { LitElement, css, html } from 'lit';
import { createFolder, getFolder, removeFolder, updateFolder } from '../helpers/api/folders-api';
import { customElement, property, query, state } from 'lit/decorators';

import { AddFolderMenuType } from './add-edit-folder-modal';
import { Dialog } from '@material/mwc-dialog';
import { SettingDrawer } from './mobile/setting-drawer';
import { detectMobileViewport } from '../helpers/device';
import { getRolesForEntity } from '../helpers/api/roles-api';

@customElement('folder-selector')
export class FolderSelector extends LitElement {
    private readonly amplitude_callsite: string = 'Folder Selector';

    @query('#deleteFolderModalComponent')
    deleteFolderModalComponent!: Dialog;

    @query('#addRenameModalComponent')
    addRenameModalComponent!: Dialog;

    @query('#manageAccessComponent')
    manageAccessComponent?: Dialog;

    @query('#accessFolderDrawer')
    accessFolderDrawer?: SettingDrawer;

    @property({ type: Array })
    folders: GetFolderResponse[] = [];

    @property({ type: Boolean })
    isModal: boolean = false;

    @property({ type: String })
    selectedFolderId?: string;

    @state()
    selectedFolderInfo?: GetFolderResponse;

    @state()
    activeMenuType?: AddFolderMenuType;

    @state()
    roles?: RoleAssignment[];

    private handleSelectFolder(e: Event, folderId: ObjectId) {
        e.preventDefault();
        e.stopPropagation();

        this.selectedFolderId = folderId.$oid;

        this.dispatchEvent(new CustomEvent('on-folder-select', {
            detail: {
                folderId: folderId.$oid,
            }
        }));
    }

    private async updateManageFolderAccess() {
        if (this.selectedFolderId === undefined) return;
        this.roles = await getRolesForEntity(
            this.amplitude_callsite,
            this.selectedFolderId,
            EntityType.Folder
        );

        this.requestUpdate();
    }

    private async triggerManageFolderAccess(selectedFolderId: string, selectedFolderInfo: GetFolderResponse) {
        this.selectedFolderId = selectedFolderId ;
        this.selectedFolderInfo = selectedFolderInfo;
        this.roles = await getRolesForEntity(this.amplitude_callsite, selectedFolderId, EntityType.Folder);

        if (detectMobileViewport()) {
            await this.accessFolderDrawer?.open();
            if (this.manageAccessComponent) {
                this.manageAccessComponent.open = false;
            }
        } else if (this.manageAccessComponent){
            this.manageAccessComponent.open = true;
        }
    }

    private async updateFolderInfo(e: any) {
        if (e.detail.folderId !== undefined) {
            const folderRequest = {
                name: e.detail.name,
            };

            await updateFolder(
                this.amplitude_callsite,
                e.detail.folderId,
                folderRequest as CreateFolderRequest,
                {}
            );

            const updatedFolders = this.folders.map(folder => {
                if(folder._id.$oid === e.detail.folderId) {
                    folder.name = e.detail.name;
                    return folder;
                }

                return folder;
            });

            this.dispatchEvent(new CustomEvent('on-update-folders', {
                detail: {
                    folders: updatedFolders
                }
            }));
        }

        this.addRenameModalComponent.close();
    }

    private async addFolder(e: any) {
        const folderRequest = {
            name: e.detail.name,
            organization_id: undefined,
            authorizations: undefined,
            parent_folder_id: e.detail.folderId,
        };

        const addResult = await createFolder(
            this.amplitude_callsite,
            folderRequest,
            {}
        );

        const updatedFolders = this.folders.map(folder => {
            if(folder._id.$oid === e.detail.folderId) {
                folder.children.push({
                    ...addResult,
                    child_type: ChildType.Folder,
                });
                return folder;
            }

            return folder;
        });

        const folderData = await getFolder(this.amplitude_callsite, addResult._id.$oid);

        updatedFolders.push(folderData);

        this.dispatchEvent(new CustomEvent('on-update-folders', {
            detail: {
                folders: updatedFolders
            }
        }));

        this.addRenameModalComponent.close();
    }


    private async deleteFolder(folderId: string | undefined) {
        if (folderId) {
            await removeFolder(this.amplitude_callsite, folderId);

            const updatedFolders =  this.folders.filter(folder => folder._id.$oid !== folderId);
            this.dispatchEvent(new CustomEvent('on-update-folders', {
                detail: {
                    folders: updatedFolders
                }
            }));
        }
        this.deleteFolderModalComponent?.close();
    }

    private renderSingleFolderView(folder: any) {
        const folderChildren = folder?.children.filter(
            (c: ChildEntity) => c.child_type.toLowerCase() === ChildType.Folder
        );
        const hasChildren = folderChildren.length > 0;
        const hasAncestors = folder?.ancestors.length > 0;
        const selectedFolder = folder?._id.$oid === this.selectedFolderId;

        return html`
            <folder-accordion ?hasChildren=${hasChildren} ?hasAncestors=${hasAncestors}>
                <div slot="heading" class="folder-selector__wrapper ${selectedFolder ? 'selected' : ''}">
                    <div class="folder-selector__name" @click=${(e: Event) => this.handleSelectFolder(e, folder._id)}>
                        <span>${folder?.name}</span>
                    </div>
                    <div class="folder-selector__menu">
                        ${!this.isModal
                            ? html`
                                  <folder-menu
                                      ?isOwner="${folder.is_owner}"
                                      ?isWriter="${folder.is_writer}"
                                      @add-sub-folder=${(e: any) => { this.selectedFolderId = folder._id.$oid; this.activeMenuType = 'add-sub-folder'; this.addRenameModalComponent.open = true;}}
                                      @rename-folder=${(e: any) => { this.selectedFolderId = folder._id.$oid; this.activeMenuType = 'rename-folder'; this.addRenameModalComponent.open = true;}}
                                      @delete-folder=${(e: any) => { this.selectedFolderId = folder._id.$oid; this.deleteFolderModalComponent.open = true;}}
                                      @manage-folder-access=${async (e: any) => await this.triggerManageFolderAccess(folder._id.$oid, folder)}>
                                  </folder-menu>
                              `
                            : ''}
                    </div>
                </div>
                <div slot="content">
                    ${folderChildren.length > 0
                        ? html`
                              ${folderChildren.map((child: any) => {
                                  const childFolder = this.folders.find(
                                      (f) => f._id.$oid === child._id.$oid
                                  );
                                  return this.renderSingleFolderView(childFolder);
                              })}
                          `
                        : ''}
                </div>
            </folder-accordion>
        `;
    }

    render() {
        return html`
            ${this.folders.filter(
                (f: GetFolderResponse) =>
                    f.ancestors.length === 0 ||
                    !f.ancestors.some((a) =>
                        this.folders
                            ?.map((obj: GetFolderResponse) => obj._id.$oid)
                            .includes(a.$oid)
                ))
                .map((f: GetFolderResponse) => this.renderSingleFolderView(f))}

            <mwc-dialog id="deleteFolderModalComponent" class="dialog" hideActions>
                <modal-dialog size="lg" @close="${() => this.deleteFolderModalComponent?.close()}">
                    <confirm-modal
                        slot="body"
                        heading="Delete Confirm"
                        content="Are you sure you want to delete this folder?"
                        cancelButtonText="Cancel"
                        submitButtonText="Delete"
                        @confirm="${async () => await this.deleteFolder(this.selectedFolderId)}"
                        @close="${() => this.deleteFolderModalComponent?.close()}">
                    </confirm-modal>
                </modal-dialog>
            </mwc-dialog>

            ${detectMobileViewport() ?
                html`<setting-drawer id="accessFolderDrawer">
                        <manage-folder-access-component
                            .folder=${this.selectedFolderInfo}
                            .folder_id=${this.selectedFolderId}
                            .roles="${this.roles}"
                            @updated=${async () => await this.updateManageFolderAccess()}>
                        </manage-folder-access-component>
                    </setting-drawer>` :
                html`<mwc-dialog id="manageAccessComponent" class="dialog" hideActions>
                        <modal-dialog size="md" @close="${() => this.manageAccessComponent?.close()}">
                            <manage-folder-access-component
                                slot="body"
                                .folder=${this.selectedFolderInfo}
                                .folder_id=${this.selectedFolderId}
                                .roles="${this.roles}"
                                @updated=${async () => await this.updateManageFolderAccess()}>
                            </manage-folder-access-component>
                        </modal-dialog>
                    </mwc-dialog>`
            }

            <mwc-dialog id="addRenameModalComponent" class="dialog" hideActions>
                <modal-dialog
                    size="lg"
                    @close="${() => this.addRenameModalComponent?.close()}">
                    <add-edit-folder-modal
                        id="addRenameModalContent"
                        slot="body"
                        .type="${this.activeMenuType}"
                        @add-sub-folder="${async (e: any) =>
                            await this.addFolder({
                                ...e,
                                detail: { ...e.detail, folderId: this.selectedFolderId },
                            })}"
                        @rename-folder="${async (e: any) =>
                            await this.updateFolderInfo({
                                ...e,
                                detail: { ...e.detail, folderId: this.selectedFolderId },
                            })}"
                        @close="${() => this.addRenameModalComponent?.close()}">
                    </add-edit-folder-modal>
                </modal-dialog>
            </mwc-dialog>

        `;
    }

    static get styles() {
        return css`
            .backpack-sidebar__backpack-page-link {
                display: flex;
                color: var(--main-primary-color);
                cursor: pointer;
                width: 100%;
                color: var(--main-primary-color);
                text-decoration: none;
                cursor: pointer;
                padding-bottom: 14px;
                flex-direction: row;
                align-content: space-between;
                align-items: center;
            }

            .folder-selector__icon {
                margin-right: 0.5rem;
                cursor: pointer;
                --mdc-icon-button-size: 1rem;
            }

            .folder-selector__wrapper {
                display: flex;
                align-items: center;
                color: var(--main-primary-color);
                cursor: pointer;
                width: 100%;
            }

            .folder-selector__wrapper.selected {
                color: var(--main-secondary-color);
            }

            .backpack-sidebar__icon {
                --mdc-icon-size: 28px;
                margin-right: 7px;
                margin-left: 2px;
                cursor: pointer;
                vertical-align: middle;
                --mdc-icon-button-size: 2.5rem;
            }

            .folder-selector__name {
                display: flex;
                align-items: center;
                flex: 1;
                padding: 0.25rem 0;
                font-size: 12px;
                margin-left: 40px;
            }
        `;
    }
}
